import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CustomerLogoCarousel = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/customer-logos`);
      setLogos(response.data);
    } catch (error) {
      console.error('Failed to fetch logos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (logos.length === 0) {
    return null; // Don't show section if no logos
  }

  // Duplicate logos for infinite scroll effect only if we have enough logos
  // If less than 5 logos, duplicate more times for smooth animation
  let duplicatedLogos = [];
  if (logos.length < 5) {
    // Duplicate 4 times for smoother scroll with fewer logos
    duplicatedLogos = [...logos, ...logos, ...logos, ...logos];
  } else {
    // Duplicate 2 times for standard infinite scroll
    duplicatedLogos = [...logos, ...logos];
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Trusted by Leading Organizations
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Proudly serving clients across industries with world-class training solutions
          </p>
        </motion.div>

        <div className="relative overflow-hidden">
          {/* Gradient overlays for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10"></div>

          {/* Infinite scroll animation */}
          <motion.div
            className="flex gap-12 items-center"
            animate={{
              x: [0, -160 * logos.length], // Adjusted for logo width + gap
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: Math.max(logos.length * 4, 15), // Slower for fewer logos, min 15 seconds
                ease: "linear",
              },
            }}
          >
            {duplicatedLogos.map((logo, index) => {
              // Helper function to determine if URL is absolute
              const getImageUrl = (url) => {
                // If URL starts with http:// or https://, it's already absolute
                if (url.startsWith('http://') || url.startsWith('https://')) {
                  return url;
                }
                // Otherwise, prepend backend URL for relative paths
                return `${BACKEND_URL}${url}`;
              };

              const LogoContent = () => (
                <img
                  src={getImageUrl(logo.logo_url)}
                  alt={logo.company_name}
                  className="max-w-full max-h-full object-contain mx-auto"
                  loading="lazy"
                  onError={(e) => {
                    // Replace broken image with company name
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="flex items-center justify-center w-full h-full border-2 border-gray-300 rounded-lg bg-white px-4">
                        <span class="text-gray-700 font-semibold text-sm text-center">${logo.company_name}</span>
                      </div>
                    `;
                  }}
                />
              );

              return (
                <div
                  key={`${logo.id}-${index}`}
                  className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300"
                >
                  {logo.website_url ? (
                    <a
                      href={logo.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full flex items-center justify-center"
                    >
                      <LogoContent />
                    </a>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <LogoContent />
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
